import { Stack, Typography, Box, Paper, Icon } from '@ace-clublabs/shared-component-library';
import { MembershipLevel, MembershipTitles } from '@lib/types';
import { ReactNode, useEffect, useState } from 'react';
export interface IBenefitChartProps {
  benefitTitle: MembershipLevel;
  promotionalLabel: string;
  className?: string;
  benefitOnClick?: () => void;
  selected?: boolean;
  children?: ReactNode;
  isDesktop?: boolean;
}

export const BenefitChartItem = ({
  benefitTitle,
  promotionalLabel,
  className,
  benefitOnClick,
  selected,
  isDesktop,
  children,
}: IBenefitChartProps) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 769);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 769);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const iconMap = (membershipLevel: MembershipLevel) => {
    switch (membershipLevel) {
      case MembershipLevel.CLASSIC:
        return (
          <Icon
            iconName="heart-solid"
            sx={{ color: '#B0c4DE', fontSize: '1.25rem', stroke: 'rgb(1, 107, 205)', strokeWidth: '2' }}
          />
        );
      case MembershipLevel.PLUS:
        return <Icon iconName="award-duotone" sx={{ color: '#BE3317', fontSize: '1.25rem' }} />;
      case MembershipLevel.PREMIER:
        return <Icon iconName="gem-duotone" sx={{ color: '#7527B9', fontSize: '1.25rem' }} />;
      default:
        return '';
    }
  };

  return (
    <Stack direction="column" className={`${className}-container`} flex={1}>
      <Paper
        elevation={8}
        className={`membership-level-card-${benefitTitle}`}
        sx={(theme) => ({
          overflow: 'hidden',
          position: 'relative',
          cursor: 'pointer',
          minWidth: '100px',
          minHeight: '84px',
          width: '100%',
          border: '2px solid transparent',
          borderColor: selected ? '#016BCD' : 'transparent',
          borderRadius: '8px',
          boxSizing: 'border-box !important',
          flex: 1,
          [theme.breakpoints.up('lg')]: {
            border: 'none',
            borderRadius: '16px',
          },
        })}
        onClick={benefitOnClick}
      >
        <Stack direction="column">
          <Box
            sx={(theme) => ({
              backgroundColor: selected ? '#ECF5FF' : 'inherit',
              [theme.breakpoints.up('lg')]: {
                backgroundColor:
                  benefitTitle === MembershipLevel.CLASSIC
                    ? '#ECF5FF'
                    : benefitTitle === MembershipLevel.PLUS
                    ? '#FEF4F2'
                    : '#FAF5FF',
              },
            })}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              gap="0.5rem"
              sx={(theme) => ({
                height: '2.125rem',
                [theme.breakpoints.up('lg')]: {
                  height: '2.75rem',
                },
              })}
            >
              {isDesktop && iconMap(benefitTitle)}
              <Typography
                variant="b2Medium"
                whiteSpace="nowrap"
                className={`${className}-promotional-label`}
                sx={(theme) => ({
                  justifySelf: 'center',
                  padding: '0.5rem 0',
                  color: selected ? '#016BCD' : '#876647',
                  [theme.breakpoints.up('lg')]: {
                    color:
                      benefitTitle === MembershipLevel.CLASSIC
                        ? '#016BCD'
                        : benefitTitle === MembershipLevel.PLUS
                        ? '#BE3317'
                        : '#7527B9',
                  },
                })}
              >
                {promotionalLabel}
              </Typography>
            </Stack>
          </Box>
          <Typography
            variant="b1Medium"
            className={`${className}-title`}
            sx={{
              justifySelf: 'center',
              textAlign: 'center',
              marginTop: isDesktop ? '1.5rem' : '0.625rem',
              marginBottom: isDesktop ? 0 : '1rem',
              lineHeight: '2rem',
            }}
          >
            {MembershipTitles[benefitTitle]}
            {benefitTitle !== MembershipLevel.CLASSIC && (
              <sup
                style={{
                  display: 'inline-block',
                  verticalAlign: 'baseline',
                  lineHeight: 0,
                  fontSize: '0.8rem',
                  transform: 'translateY(-0.25em)',
                  marginLeft: '0.125rem',
                }}
              >
                ®
              </sup>
            )}
          </Typography>
        </Stack>
        {isDesktop && <Box sx={{ padding: '0 1rem 1.5rem 1rem', marginTop: 'auto' }}>{children}</Box>}
      </Paper>
    </Stack>
  );
};
